import * as React from 'react';
import { observer } from 'mobx-react';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { Employee } from 'nomst/Stores/Decision/employee';
import { Amount as AmountBusinessLogic } from 'nomst/Stores/Decision/amount';
import { AmountCode } from 'typings/enums';
import CompensationSummary from 'nomst/Components/Decision/EmployeeCardDetails/compensationSummary';
import Amount from 'nomst/Components/Decision/amount';
import StockAmount from 'nomst/Components/Decision/stockAmount';
import { AmountCodeDisplayOrdinal } from 'typings/amountCode';
import { ICountryDecisionDispatcher } from 'typings/countryDecision.dispatcher';
import { compareItem } from 'common/compareItem';
import { MultifFactorBonusView } from '../multiFactorBonusView';
import { EligibilityProrationModal } from './eligibilityProrationModal';
import { User } from 'nomst/Stores/User/user';
import { eligibilityOverride } from 'utils/MobxDataManager/singletons';
import './employeeCycleInfo.scss';

export interface IEmployeeCycleInfoProps {
    configuration: Configuration;
    countryDecisionDispatcher: Nullable<ICountryDecisionDispatcher>;
    employee: Employee;
    user: User;
    isCycleLocked: boolean;
    showMultiComponentBonus?: boolean;
}

interface IEmployeeCycleInfoState {
    showModal: boolean;
}

export class EmployeeCycleInfo extends React.Component<IEmployeeCycleInfoProps, IEmployeeCycleInfoState> {
    constructor(props: IEmployeeCycleInfoProps) {
        super(props);
        this.renderAmount = this.renderAmount.bind(this);
        this.renderMultiFactorBonus = this.renderMultiFactorBonus.bind(this);
        this.handleModalOpen = this.handleModalOpen.bind(this);
        this.handleModalClose = this.handleModalClose.bind(this);
        this.state = Object.assign({showModal: false});
    }

    render(): React.ReactNode {
        const { employee, configuration } = this.props;
        return (
            <div className="nomst-employee-cycle-info">
                {this.renderEmployeeCycleInfo()}
                <CompensationSummary
                    employee={employee}
                    configuration={configuration}
                />
            </div>
        );
    }

    private renderEmployeeCycleInfo(): JSX.Element {
        const { configuration, employee, user } = this.props;
        const overrideLinkText = 'Eligibility & Proration Override';
        const hideModalLink: boolean = !configuration.cycleBudgetFields
            || configuration.cycleBudgetFields.length === 0
            || configuration.isClosed
            || configuration.isClosing
            || user.isRoleOnlyManager;

        return (
            <div className="nomst-employee-cycle-info__current-cycle">
                <h3 className="title">Current Cycle</h3>
                {
                    this.sortedAmounts.map(this.renderAmount)
                }
                {this.renderStockAmount()}
                <p
                    hidden={!employee.isHourly}
                    className="hourly-employee-notes"
                >
                    * Lump Sum, Bonus and TCC are calculated based on annual pay
                </p>
                <button
                    onClick={this.handleModalOpen}
                    hidden={hideModalLink}
                    className="nomst-employee-cycle-info__proration-modal-trigger pxl-link"
                >
                    <i className="icon-pencil" />
                    {overrideLinkText}
                </button>
                {this.state.showModal &&
                    (
                        <EligibilityProrationModal
                            countryCurrency={configuration.homeCountryCurrency}
                            configuration={configuration}
                            user={user}
                            closeModal={this.handleModalClose}
                            eligibilityOverride={eligibilityOverride}
                            employee={employee}
                        />
                    )
                }
            </div>
        );
    }

    private handleModalOpen(): void {
        const { employee } = this.props;
        if (employee.stockAmount) {
            eligibilityOverride.setEmployeeStockAmount(employee.stockAmount);
        }
        eligibilityOverride.setEmployeeAmounts(employee.amounts);
        this.setState({showModal: true});
    }

    private handleModalClose(): void {
        eligibilityOverride.resetEmployeeAmounts();
        this.setState({showModal: false});
    }

    private renderAmount(amount: AmountBusinessLogic): JSX.Element {
        const {
            configuration,
            countryDecisionDispatcher,
            employee,
            showMultiComponentBonus,
            isCycleLocked
        } = this.props;
        const cycleBudgetField = configuration.getCycleBudgetField(amount.amountCode);
        const isAmountReadOnly = cycleBudgetField.amountCode === AmountCode.Bonus && showMultiComponentBonus;
        const isLumpSumReadOnly = amount.amountCode === AmountCode.LumpSum
            && !cycleBudgetField.rangeEnforcement.allowOverrideLumpSum;
        return (
            <div key={amount.amountCode} className="amount-container">
                <Amount
                    configuration={configuration}
                    employeeSourceId={employee.employeeSourceId}
                    amount={amount}
                    countryDecisionDispatcher={countryDecisionDispatcher}
                    title={cycleBudgetField ? cycleBudgetField.title : ''}
                    isHourly={employee.isHourly}
                    annualStandardWorkHours={employee.annualStandardWorkHours}
                    fullTimeEquivalent={employee.fullTimeEquivalent}
                    isReadOnly={isCycleLocked || !!isAmountReadOnly || isLumpSumReadOnly}
                    idSuffix="cycle-info"
                />
                {/* this.renderMultiFactorBonus(amount) */}
                {/* TODO: Re-enable this once multi-comp bonus is finished PSP-21488*/}
            </div>
        );
    }

    private renderStockAmount(): React.ReactNode {
        const { configuration, employee, countryDecisionDispatcher, isCycleLocked } = this.props;
        if (!employee.stockAmount) {
            return null;
        }

        return (
            <div className="amount-container">
                <StockAmount
                    employeeSourceId={employee.employeeSourceId}
                    stockAmount={employee.stockAmount}
                    configuration={configuration}
                    countryDecisionDispatcher={countryDecisionDispatcher}
                    isReadOnly={isCycleLocked}
                />
            </div>
        );
    }

    private renderMultiFactorBonus(amount: AmountBusinessLogic): React.ReactNode {
        const { configuration, employee } = this.props;

        // hack week get the actual flag from budget or configuration
        const bonusFieldDecisions = employee.bonusFieldDecisions && employee.bonusFieldDecisions.length
            ? employee.bonusFieldDecisions
            : configuration.bonusFieldRules;

        const cycleBudgetField = configuration.getCycleBudgetField(amount.amountCode);
        return this.props.showMultiComponentBonus
        &&  cycleBudgetField.amountCode === AmountCode.Bonus
        && amount.isEligible ?
            (
                <MultifFactorBonusView
                    configuration={configuration}
                    employeeSourceId={employee.employeeSourceId}
                    employeeDispatcher={employee.employeeDispatcher}
                    bonusFieldDecisions={bonusFieldDecisions}
                    amount={amount}
                />
            )
            : null;
    }

    private get sortedAmounts(): AmountBusinessLogic[] {
        const { employee } = this.props;
        return employee.amounts
            .filter(amount => amount.amountCode !== AmountCode.Promotion)
            .sort((amount, nextAmount) => compareItem(
                AmountCodeDisplayOrdinal[amount.amountCode],
                AmountCodeDisplayOrdinal[nextAmount.amountCode]));
    }
}

export default observer(EmployeeCycleInfo);